import { Stack } from '@mui/material';

import {
  Card,
  CardContent,
  CardHeader,
  EmptyDataTable,
  HelpOutlineIcon,
  IPaginationProps,
  LinearProgress,
  LoadedContent,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  Typography,
} from '@occc/ui-design-system';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import { Segment_Upload_File_Status, SegmentUploadedFileFragment } from '@occc/graphql';
import { formatDateTime, parseDate, DateTimeFormat } from '@occc-app/lib/date';

import { labelByUploadFilesStatus, labelByUploadFileMode } from '../../../segments.constants';

export type SegmentCsvFilesCardProps = {
  filesData: LoadedData<SegmentUploadedFileFragment[]>;
  paginationProps: IPaginationProps;
};

export const SegmentCsvFilesCard = ({ filesData, paginationProps }: SegmentCsvFilesCardProps) => {
  const { loading, error, data } = filesData;

  const renderEmpty = () => {
    return (
      <Card>
        <CardHeader title={<Typography variant='subtitle1'>CSV Files</Typography>} />
        <CardContent>
          <EmptyDataTable testId='table-empty-message' />
        </CardContent>
      </Card>
    );
  };

  const renderData = () => {
    return (
      <TableContainer>
        <TableHeader title='CSV Files' />

        {loading && <LinearProgress />}

        <Table>
          <TableHead>
            <TableCell>Name</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Upload Status</TableCell>
            <TableCell>Date</TableCell>
          </TableHead>

          <TableBody>
            {data?.map((file) => {
              return (
                <TableRow key={file.uuid}>
                  <TableCell>{file.name}</TableCell>

                  <TableCell>
                    <Typography variant='body2'>
                      {file.mode ? labelByUploadFileMode[file.mode] : 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {file.status !== Segment_Upload_File_Status.AudienceUpdatePending && (
                      <Typography variant='body2'>
                        {labelByUploadFilesStatus[file.status]}
                      </Typography>
                    )}

                    {file.status === Segment_Upload_File_Status.AudienceUpdatePending && (
                      <Stack direction='row' alignItems='center'>
                        <Typography variant='body2' mr={0.5}>
                          {labelByUploadFilesStatus[file.status]}
                        </Typography>

                        <Tooltip
                          title={
                            'Due to the size, audiences associated to this segment will process tonight'
                          }
                          placement='top'
                        >
                          <HelpOutlineIcon
                            sx={{ height: 16, width: 16, color: 'text.secondary' }}
                          />
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {formatDateTime(parseDate(file.createdAt), DateTimeFormat.DateTime12h)}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Pagination {...paginationProps} />
      </TableContainer>
    );
  };

  return (
    <LoadedContent<SegmentUploadedFileFragment>
      loading={loading}
      error={error}
      items={data}
      renderData={renderData}
      renderEmpty={renderEmpty}
    />
  );
};
